import { formatMandatePct } from "@repo/shared";
import type { ClientMandateOrdersResponse } from "@repo/shared/types";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { OrderFlowGroup } from "@/components/orders/order-flow-group";
import { OrderListToolbar } from "@/components/orders/order-list-toolbar";
import { OrderStatusBadge } from "@/components/orders/order-status-badge";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./mandate-orders-section.i18n";

type Props = {
  data: ClientMandateOrdersResponse;
  onMutate: () => void;
};

export function MandateOrdersSection({ data, onMutate }: Props) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedFlows, setExpandedFlows] = useState<Set<string>>(new Set());

  const toggleFlow = (id: string) => {
    setExpandedFlows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter orders and remove empty groups
  const filteredFlows = data.flowInstances
    .map((fi) => ({
      ...fi,
      orders:
        statusFilter === "all" ? fi.orders : fi.orders.filter((o) => o.status === statusFilter),
    }))
    .filter((fi) => fi.orders.length > 0);

  const totalOrders = data.flowInstances.reduce((sum, fi) => sum + fi.orders.length, 0);

  if (totalOrders === 0) {
    return <EmptyState>{t.empty}</EmptyState>;
  }

  return (
    <div className="space-y-4">
      <OrderListToolbar
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onRefresh={onMutate}
        language={userLanguage}
      />

      {filteredFlows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">{t.noOrdersFilter}</p>
      ) : (
        <div className="space-y-3">
          {filteredFlows.map((fi) => (
            <OrderFlowGroup
              key={fi.flowInstanceId}
              flowName={fi.flowName}
              completedAt={fi.completedAt}
              summary={`${fi.orders.length} ${fi.orders.length !== 1 ? t.orders : t.order}`}
              expanded={expandedFlows.has(fi.flowInstanceId)}
              onToggle={() => toggleFlow(fi.flowInstanceId)}
              language={userLanguage}
            >
              {fi.orders.map((order) => (
                <div key={order.id} className="space-y-2">
                  {/* Order heading: the mandate, its goal, the bank side and the order status */}
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-medium">{order.name}</h4>
                        {order.goalName && (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            {order.goalName}
                          </span>
                        )}
                        {order.mandateStatus === "pending" && (
                          <StatusBadge status="pending">{t.pendingMandate}</StatusBadge>
                        )}
                        {order.mandateStatus === "active" && (
                          <StatusBadge status="active">
                            {t.activeMandate}
                            {order.mandateExternalId ? ` · ${order.mandateExternalId}` : ""}
                          </StatusBadge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {order.origin === "updated" ? t.originUpdated : t.originCreated}
                        {" · "}
                        {order.mode === "fixed" ? t.modeFixed : t.modeMinMax}
                        {" · "}
                        {t.lines(order.lines.length)}
                        {order.replacesMandateExternalId &&
                          ` · ${t.replaces(order.replacesMandateExternalId)}`}
                      </div>
                    </div>
                    {order.externalOrderId && (
                      <span className="hidden md:inline text-xs text-muted-foreground font-mono pt-0.5">
                        {t.externalOrder} {order.externalOrderId}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 shrink-0">
                      <OrderStatusBadge status={order.status} language={userLanguage} />
                      {order.statusReason &&
                        ["failed", "cancelled", "partially_executed"].includes(order.status) && (
                          <TriangleAlert
                            className="h-3.5 w-3.5 shrink-0 text-warning"
                            aria-label={order.statusReason}
                          >
                            <title>{order.statusReason}</title>
                          </TriangleAlert>
                        )}
                    </span>
                  </div>

                  <Table variant="data" surface={false}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t.instrument}</TableHead>
                        <TableHead className="w-[140px]">{t.isin}</TableHead>
                        {order.mode === "fixed" ? (
                          <TableHead className="text-right w-[110px]">{t.weight}</TableHead>
                        ) : (
                          <>
                            <TableHead className="text-right w-[90px]">{t.min}</TableHead>
                            <TableHead className="text-right w-[90px]">{t.defaultW}</TableHead>
                            <TableHead className="text-right w-[90px]">{t.max}</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {order.lines.map((l) => (
                        <TableRow key={`${l.isin ?? l.name}-${l.order}`}>
                          <TableCell className="font-medium">{l.name ?? l.isin}</TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {l.isin ?? "—"}
                          </TableCell>
                          {order.mode === "fixed" ? (
                            <TableCell className="text-right">
                              {formatMandatePct(l.weightPct)}
                            </TableCell>
                          ) : (
                            <>
                              <TableCell className="text-right">
                                {formatMandatePct(l.minPct)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatMandatePct(l.defaultPct)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatMandatePct(l.maxPct)}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{t.accounts}: </span>
                    {order.accounts.length === 0
                      ? t.noAccounts
                      : order.accounts
                          .map((a) => a.accountName ?? a.accountExternalId ?? a.accountId)
                          .join(" · ")}
                  </div>
                </div>
              ))}
            </OrderFlowGroup>
          ))}
        </div>
      )}
    </div>
  );
}
